import React, { useState } from 'react';
import { Row, Col, Typography, Card, Space, Divider, Button, Timeline } from 'antd';
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  SelectOutlined,
  CalendarOutlined,
  TeamOutlined,
  SafetyOutlined,
  DollarOutlined,
  ClockCircleOutlined,
  HomeOutlined,
  BankOutlined,
  HeartOutlined,
  PhoneOutlined,
  ThunderboltOutlined,
  TrophyOutlined,
  SmileOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { theme } from '../../styles/theme';
import { HOW_WE_WORK_CONTENT } from '../../constants/howWeWorkContent';
import { BookingModal } from '../common/ui';

const { Title, Paragraph, Text } = Typography;

const HowWeWork = () => {
  const [bookingModalVisible, setBookingModalVisible] = useState(false);

  // Icon mapping for steps
  const stepIcons = {
    select: <SelectOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
    calendar: <CalendarOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
    service: <TeamOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
    peace: <SmileOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />
  };

  // Icon mapping for customer groups
  const customerIcons = {
    'Busy Homeowners & Working Professionals': <HomeOutlined style={{ fontSize: '3rem', color: theme.colors.primary }} />,
    'Property Investors & Landlords': <BankOutlined style={{ fontSize: '3rem', color: theme.colors.primary }} />,
    'Senior Homeowners': <HeartOutlined style={{ fontSize: '3rem', color: theme.colors.primary }} />
  };

  return (
    <div style={{ background: theme.colors.white }}>
      {/* Hero Section */}
      <section style={{ 
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)`,
        padding: '80px 20px',
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Title level={1} style={{ 
            color: theme.colors.white,
            fontFamily: theme.fonts.primary,
            marginBottom: '1rem'
          }}>
            {HOW_WE_WORK_CONTENT.hero.title}
          </Title>
          <Paragraph style={{ 
            color: theme.colors.white,
            fontSize: '1.3rem',
            fontFamily: theme.fonts.primary,
            maxWidth: '800px',
            margin: '0 auto'
          }}>
            {HOW_WE_WORK_CONTENT.hero.subtitle}
          </Paragraph>
        </div>
      </section>

      {/* Value Proposition with Stats */}
      <section style={{ padding: '80px 20px', background: theme.colors.lightGray }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Title level={2} style={{ fontFamily: theme.fonts.primary }}>
              {HOW_WE_WORK_CONTENT.valueProposition.title}
            </Title>
            <Paragraph style={{ 
              fontSize: '1.2rem',
              color: theme.colors.gray,
              maxWidth: '900px',
              margin: '1rem auto',
              fontFamily: theme.fonts.primary
            }}>
              {HOW_WE_WORK_CONTENT.valueProposition.description}
            </Paragraph>
          </div>

          <Row gutter={[32, 32]} justify="center">
            {HOW_WE_WORK_CONTENT.valueProposition.highlights.map((highlight, index) => (
              <Col xs={24} sm={8} key={index}>
                <Card style={{ 
                  textAlign: 'center',
                  border: 'none',
                  boxShadow: theme.shadows.md,
                  borderRadius: theme.borderRadius.xl
                }}>
                  <Title level={1} style={{ 
                    color: theme.colors.primary,
                    fontFamily: theme.fonts.primary,
                    margin: 0,
                    fontSize: '3.5rem'
                  }}>
                    {highlight.stat}
                  </Title>
                  <Text style={{ 
                    fontSize: '1.1rem',
                    fontFamily: theme.fonts.primary,
                    color: theme.colors.gray
                  }}>
                    {highlight.label}
                  </Text>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Problem vs Solution - Visual Comparison */}
      <section style={{ padding: '80px 20px', background: theme.colors.white }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Title level={2} style={{ fontFamily: theme.fonts.primary }}>
              {HOW_WE_WORK_CONTENT.problemSolution.title}
            </Title>
          </div>

          <Row gutter={[32, 32]}>
            {/* Traditional Way */}
            <Col xs={24} md={12}>
              <Card style={{ 
                height: '100%',
                border: `2px solid ${theme.colors.error}`,
                borderRadius: theme.borderRadius.xl,
                background: '#fff5f5'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <CloseCircleOutlined style={{ 
                    fontSize: '3rem', 
                    color: theme.colors.error,
                    marginBottom: '1rem'
                  }} />
                  <Title level={3} style={{ 
                    fontFamily: theme.fonts.primary,
                    color: theme.colors.error
                  }}>
                    {HOW_WE_WORK_CONTENT.problemSolution.traditional.title}
                  </Title>
                  <Text style={{ 
                    fontSize: '1.1rem',
                    color: theme.colors.gray,
                    fontFamily: theme.fonts.primary
                  }}>
                    {HOW_WE_WORK_CONTENT.problemSolution.traditional.subtitle}
                  </Text>
                </div>

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {HOW_WE_WORK_CONTENT.problemSolution.traditional.problems.map((problem, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <CloseCircleOutlined style={{ color: theme.colors.error, fontSize: '1.2rem', marginTop: '4px' }} />
                      <Text style={{ fontFamily: theme.fonts.primary, fontSize: '1rem' }}>{problem}</Text>
                    </div>
                  ))}
                </Space>
              </Card>
            </Col>

            {/* HSP Way */}
            <Col xs={24} md={12}>
              <Card style={{ 
                height: '100%',
                border: `2px solid ${theme.colors.success}`,
                borderRadius: theme.borderRadius.xl,
                background: '#f6ffed'
              }}>
                <div style={{ textAlign: 'center', marginBottom: '2rem' }}>
                  <CheckCircleOutlined style={{ 
                    fontSize: '3rem', 
                    color: theme.colors.success,
                    marginBottom: '1rem'
                  }} />
                  <Title level={3} style={{ 
                    fontFamily: theme.fonts.primary,
                    color: theme.colors.success
                  }}>
                    {HOW_WE_WORK_CONTENT.problemSolution.hspWay.title}
                  </Title>
                  <Text style={{ 
                    fontSize: '1.1rem',
                    color: theme.colors.gray,
                    fontFamily: theme.fonts.primary
                  }}>
                    {HOW_WE_WORK_CONTENT.problemSolution.hspWay.subtitle}
                  </Text>
                </div>

                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  {HOW_WE_WORK_CONTENT.problemSolution.hspWay.solutions.map((solution, index) => (
                    <div key={index} style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
                      <CheckCircleOutlined style={{ color: theme.colors.success, fontSize: '1.2rem', marginTop: '4px' }} />
                      <Text style={{ fontFamily: theme.fonts.primary, fontSize: '1rem' }}>{solution}</Text>
                    </div>
                  ))}
                </Space>
              </Card>
            </Col>
          </Row>
        </div>
      </section>

      {/* How It Works - 4 Steps */}
      <section style={{ padding: '80px 20px', background: theme.colors.lightGray }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Title level={2} style={{ fontFamily: theme.fonts.primary }}>
              {HOW_WE_WORK_CONTENT.howItWorks.title}
            </Title>
            <Paragraph style={{ 
              fontSize: '1.2rem',
              color: theme.colors.gray,
              fontFamily: theme.fonts.primary
            }}>
              {HOW_WE_WORK_CONTENT.howItWorks.subtitle}
            </Paragraph>
          </div>

          <Row gutter={[32, 32]}>
            {HOW_WE_WORK_CONTENT.howItWorks.steps.map((step, index) => (
              <Col xs={24} sm={12} lg={6} key={index}>
                <Card style={{ 
                  height: '100%',
                  textAlign: 'center',
                  border: 'none',
                  boxShadow: theme.shadows.md,
                  borderRadius: theme.borderRadius.xl,
                  position: 'relative'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: '-20px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '50px',
                    height: '50px',
                    borderRadius: '50%',
                    background: theme.colors.primary,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontFamily: theme.fonts.primary,
                    fontSize: '1.5rem',
                    fontWeight: 'bold',
                    color: theme.colors.white
                  }}>
                    {step.number}
                  </div>
                  
                  <div style={{ marginTop: '40px', marginBottom: '1.5rem' }}>
                    {stepIcons[step.icon]}
                  </div>
                  
                  <Title level={4} style={{ 
                    fontFamily: theme.fonts.primary,
                    marginBottom: '1rem'
                  }}>
                    {step.title}
                  </Title>
                  
                  <Paragraph style={{ 
                    fontFamily: theme.fonts.primary,
                    color: theme.colors.gray
                  }}>
                    {step.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
        </div>
      </section>

      {/* Customer Benefits - Detailed for Each Group */}
      <section style={{ padding: '80px 20px', background: theme.colors.white }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Title level={2} style={{ fontFamily: theme.fonts.primary }}>
              {HOW_WE_WORK_CONTENT.customerBenefits.title}
            </Title>
            <Paragraph style={{ 
              fontSize: '1.2rem',
              color: theme.colors.gray,
              fontFamily: theme.fonts.primary
            }}>
              {HOW_WE_WORK_CONTENT.customerBenefits.subtitle}
            </Paragraph>
          </div>

          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {HOW_WE_WORK_CONTENT.customerBenefits.groups.map((group, groupIndex) => (
              <Card key={groupIndex} style={{ 
                border: 'none',
                boxShadow: theme.shadows.lg,
                borderRadius: theme.borderRadius.xl
              }}>
                <Row gutter={[32, 32]} align="middle">
                  <Col xs={24} md={6} style={{ textAlign: 'center' }}>
                    {customerIcons[group.type]}
                    <Title level={4} style={{ 
                      fontFamily: theme.fonts.primary,
                      marginTop: '1rem',
                      marginBottom: '0.5rem'
                    }}>
                      {group.type}
                    </Title>
                    <Text style={{ 
                      fontSize: '1.1rem',
                      color: theme.colors.primary,
                      fontFamily: theme.fonts.primary,
                      fontWeight: 'bold'
                    }}>
                      {group.tagline}
                    </Text>
                  </Col>

                  <Col xs={24} md={18}>
                    <Paragraph style={{ 
                      fontSize: '1.1rem',
                      fontFamily: theme.fonts.primary,
                      marginBottom: '1.5rem',
                      fontWeight: '500'
                    }}>
                      {group.mainBenefit}
                    </Paragraph>

                    <Row gutter={[16, 16]}>
                      {group.benefits.map((benefit, benefitIndex) => (
                        <Col xs={24} sm={12} key={benefitIndex}>
                          <div style={{ 
                            display: 'flex', 
                            alignItems: 'flex-start', 
                            gap: '12px',
                            padding: '12px',
                            background: theme.colors.lightGray,
                            borderRadius: theme.borderRadius.md
                          }}>
                            <CheckCircleOutlined style={{ 
                              color: theme.colors.success, 
                              fontSize: '1.2rem',
                              marginTop: '4px'
                            }} />
                            <div>
                              <Text strong style={{ 
                                fontFamily: theme.fonts.primary,
                                display: 'block',
                                marginBottom: '4px'
                              }}>
                                {benefit.title}
                              </Text>
                              <Text style={{ 
                                fontFamily: theme.fonts.primary,
                                color: theme.colors.gray,
                                fontSize: '0.9rem'
                              }}>
                                {benefit.description}
                              </Text>
                            </div>
                          </div>
                        </Col>
                      ))}
                    </Row>

                    <div style={{ 
                      marginTop: '1.5rem',
                      padding: '16px',
                      background: `${theme.colors.primary}15`,
                      borderRadius: theme.borderRadius.md,
                      borderLeft: `4px solid ${theme.colors.primary}`
                    }}>
                      <DollarOutlined style={{ 
                        color: theme.colors.primary,
                        fontSize: '1.2rem',
                        marginRight: '8px'
                      }} />
                      <Text strong style={{ 
                        fontFamily: theme.fonts.primary,
                        color: theme.colors.primary
                      }}>
                        {group.savings}
                      </Text>
                    </div>
                  </Col>
                </Row>
              </Card>
            ))}
          </Space>
        </div>
      </section>

      {/* Why It Works */}
      <section style={{ padding: '80px 20px', background: theme.colors.lightGray }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <Title level={2} style={{ fontFamily: theme.fonts.primary }}>
              {HOW_WE_WORK_CONTENT.whyItWorks.title}
            </Title>
            <Paragraph style={{ 
              fontSize: '1.2rem',
              color: theme.colors.gray,
              fontFamily: theme.fonts.primary
            }}>
              {HOW_WE_WORK_CONTENT.whyItWorks.subtitle}
            </Paragraph>
          </div>

          <Row gutter={[32, 32]}>
            {HOW_WE_WORK_CONTENT.whyItWorks.reasons.map((reason, index) => {
              const icons = [
                <ThunderboltOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
                <SafetyOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
                <TrophyOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />,
                <SmileOutlined style={{ fontSize: '2.5rem', color: theme.colors.primary }} />
              ];

              return (
                <Col xs={24} md={12} key={index}>
                  <Card style={{ 
                    height: '100%',
                    border: 'none',
                    boxShadow: theme.shadows.md,
                    borderRadius: theme.borderRadius.xl
                  }}>
                    <div style={{ marginBottom: '1.5rem' }}>
                      {icons[index]}
                    </div>
                    
                    <Title level={4} style={{ 
                      fontFamily: theme.fonts.primary,
                      marginBottom: '1rem'
                    }}>
                      {reason.title}
                    </Title>
                    
                    <Paragraph style={{ 
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.gray,
                      marginBottom: '1rem'
                    }}>
                      {reason.description}
                    </Paragraph>

                    <div style={{ 
                      padding: '12px',
                      background: `${theme.colors.success}15`,
                      borderRadius: theme.borderRadius.md,
                      borderLeft: `3px solid ${theme.colors.success}`
                    }}>
                      <CheckCircleOutlined style={{ 
                        color: theme.colors.success,
                        marginRight: '8px'
                      }} />
                      <Text strong style={{ 
                        fontFamily: theme.fonts.primary,
                        color: theme.colors.success
                      }}>
                        {reason.benefit}
                      </Text>
                    </div>
                  </Card>
                </Col>
              );
            })}
          </Row>
        </div>
      </section>

      {/* Sources & Citations Section */}
      <section style={{ padding: '80px 20px', background: theme.colors.white }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <Title level={2} style={{ fontFamily: theme.fonts.primary }}>
              {HOW_WE_WORK_CONTENT.sources.title}
            </Title>
          </div>

          <Row gutter={[32, 32]}>
            {HOW_WE_WORK_CONTENT.sources.citations.map((citation, index) => (
              <Col xs={24} md={12} key={index}>
                <Card style={{ 
                  height: '100%',
                  border: 'none',
                  boxShadow: theme.shadows.md,
                  borderRadius: theme.borderRadius.xl
                }}>
                  <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                    <div>
                      <Text strong style={{ 
                        fontSize: '1.1rem',
                        color: theme.colors.primary,
                        fontFamily: theme.fonts.primary,
                        display: 'block',
                        marginBottom: '8px'
                      }}>
                        {citation.claim}
                      </Text>
                      <Text style={{ 
                        fontSize: '0.95rem',
                        fontFamily: theme.fonts.primary,
                        fontWeight: '600',
                        color: theme.colors.black
                      }}>
                        Source: {citation.source}
                      </Text>
                    </div>
                    
                    <Paragraph style={{ 
                      fontFamily: theme.fonts.primary,
                      color: theme.colors.gray,
                      marginBottom: '1rem'
                    }}>
                      {citation.detail}
                    </Paragraph>

                    <a 
                      href={citation.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      style={{ 
                        color: theme.colors.primary,
                        fontFamily: theme.fonts.primary,
                        fontSize: '0.9rem',
                        textDecoration: 'none'
                      }}
                    >
                      Visit {citation.source} →
                    </a>
                  </Space>
                </Card>
              </Col>
            ))}
          </Row>

          <div style={{ 
            marginTop: '3rem',
            padding: '20px',
            background: theme.colors.lightGray,
            borderRadius: theme.borderRadius.md,
            textAlign: 'center'
          }}>
            <Text style={{ 
              fontFamily: theme.fonts.primary,
              color: theme.colors.gray,
              fontSize: '0.9rem',
              fontStyle: 'italic'
            }}>
              {HOW_WE_WORK_CONTENT.sources.disclaimer}
            </Text>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section style={{ 
        padding: '80px 20px',
        background: `linear-gradient(135deg, ${theme.colors.primary} 0%, ${theme.colors.primaryLight} 100%)`,
        textAlign: 'center'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <Title level={2} style={{ 
            color: theme.colors.white,
            fontFamily: theme.fonts.primary,
            marginBottom: '1rem'
          }}>
            {HOW_WE_WORK_CONTENT.cta.title}
          </Title>
          
          <Paragraph style={{ 
            color: theme.colors.white,
            fontSize: '1.2rem',
            fontFamily: theme.fonts.primary,
            marginBottom: '2rem'
          }}>
            {HOW_WE_WORK_CONTENT.cta.subtitle}
          </Paragraph>

          <Space direction="vertical" size="large">
            <Button 
              type="primary"
              size="large"
              onClick={() => setBookingModalVisible(true)}
              style={{
                height: '55px',
                fontSize: '1.1rem',
                fontWeight: 'bold',
                borderRadius: '16px',
                padding: '0 40px',
                background: theme.colors.white,
                color: theme.colors.white,
                border: 'none',
                boxShadow: '0 8px 20px rgba(0,0,0,0.2)'
              }}
            >
              {HOW_WE_WORK_CONTENT.cta.buttonText}
            </Button>

            <div>
              <PhoneOutlined style={{ color: theme.colors.white, marginRight: '8px' }} />
              <Text style={{ 
                color: theme.colors.white,
                fontFamily: theme.fonts.primary,
                fontSize: '1.1rem'
              }}>
                {HOW_WE_WORK_CONTENT.cta.secondaryText}
              </Text>
            </div>
          </Space>
        </div>
      </section>
      <BookingModal 
        visible={bookingModalVisible} 
        onClose={() => setBookingModalVisible(false)} 
        source="how_we_work_page_quote" 
      />
    </div>
  );
};

export default HowWeWork;
